import * as React from "react";

import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import Router from "next/router";

import { PermissionContext } from "context/permissioncontext";
import { MenuButton, StyledMenuItem } from "src/common/components/header/Menu";
import { FontAwesomeIconWithTheme } from "src/common/components/ui/IconWrapper";
import { useDownloadEvents } from "src/domain/administrative/events/api/getEvents";
import {
  DOWNLOAD_PERMISSION,
  ORG_WORKFLOWS_PATH,
  ORG_WORKFLOW_TEMPLATES_PATH,
  ORG_USERS_PATH,
  ORG_DISKS_PATH,
  ORG_INSTANCE_TYPES_PATH,
  ORG_BLUEPRINTS_PATH,
  ORG_USER_GROUPS_PATH,
  ORG_CONTACT_PATH,
  ORG_WORKFLOW_CONFIG_PATH,
  ORG_AWS_CREDENTIALS_PATH,
  ORG_AWS_NETWORK_PATH,
  ORG_LICENSES_PATH,
  ORG_ACCOUNT_INFO_PATH,
  ORG_FEATURES_PATH,
} from "src/lib/const";

const OrgAdminDropdown: React.FC = () => {
  const download = useDownloadEvents();
  const permissions = React.useContext(PermissionContext);

  const [eventsDownloading, setEventsDownloading] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRoute = (route: string) => (e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();

    Router.push(route);
    setAnchorEl(null);
  };

  const onResourceEventClick = async () => {
    setEventsDownloading(true);
    await download("resource", "resource_event.sql");
    setEventsDownloading(false);
  };

  const buildMenuItems = (path: string, displayName: string) => {
    switch (path) {
      case DOWNLOAD_PERMISSION: {
        if (permissions.has(path)) {
          return (
            <StyledMenuItem onClick={onResourceEventClick} disabled={eventsDownloading}>
              {displayName}
            </StyledMenuItem>
          );
        }
        break;
      }
      default: {
        if (permissions.has(path)) {
          return <StyledMenuItem onClick={handleRoute(path)}>{displayName}</StyledMenuItem>;
        }
      }
    }

    return null;
  };

  return (
    <div>
      <MenuButton
        id="user-dropdown-button"
        aria-controls={open ? "composition-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        disableElevation
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        <FontAwesomeIconWithTheme icon={faBuilding} style={{ marginLeft: "5px", marginRight: "5px" }} />
        Admin
      </MenuButton>
      <Menu
        id="demo-customized-menu"
        MenuListProps={{
          "aria-labelledby": "demo-customized-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {buildMenuItems(ORG_USERS_PATH, "Users")}
        {buildMenuItems(ORG_DISKS_PATH, "Disks")}
        {buildMenuItems(ORG_WORKFLOWS_PATH, "Workflows")}
        {buildMenuItems(ORG_WORKFLOW_TEMPLATES_PATH, "Workflow Templates")}
        {buildMenuItems(ORG_INSTANCE_TYPES_PATH, "Instance Types")}
        <Divider sx={{ my: 0.5 }} />
        {buildMenuItems(ORG_ACCOUNT_INFO_PATH, "Account Information")}
        {buildMenuItems(ORG_CONTACT_PATH, "Contact Information")}
        {buildMenuItems(ORG_WORKFLOW_CONFIG_PATH, "Workflow Configuration")}
        {buildMenuItems(ORG_FEATURES_PATH, "Organization Features")}
        {buildMenuItems(ORG_AWS_CREDENTIALS_PATH, "AWS Credentials")}
        {buildMenuItems(ORG_AWS_NETWORK_PATH, "AWS Network")}
        {buildMenuItems(ORG_LICENSES_PATH, "Licenses")}
        {buildMenuItems(ORG_BLUEPRINTS_PATH, "Blueprint Assignments")}
        {buildMenuItems(ORG_USER_GROUPS_PATH, "User Groups")}
        <Divider sx={{ my: 0.5 }} />
        {buildMenuItems(DOWNLOAD_PERMISSION, "Download Resource Events")}
      </Menu>
    </div>
  );
};

export default OrgAdminDropdown;
